import React, { useState } from 'react';
import PropTypes from "prop-types";
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';

import moment from "moment/moment";

import Notifications from "react-notification-system-redux";

import { api } from '../../../../../api';
import store from '../../../../../createStore';
import { copyToClipboard } from '../../../../../utils';

const AMOUNT_REGEX = /^\d+([.,]\d{1,2})?$/;

const extractErrorMessage = (e) => {
  const data = e && e.response && e.response.data;
  if (data) {
    if (data.detail) {
      return data.detail;
    }
    if (data.amount) {
      return `Montant : ${[].concat(data.amount).join(' ')}`;
    }
    if (data.order_id) {
      return `Commande : ${[].concat(data.order_id).join(' ')}`;
    }
    if (data.url) {
      return `Lien : ${[].concat(data.url).join(' ')}`;
    }
  }
  return (e && e.message) || "Une erreur est survenue";
};

export const PaymentLinkModal = ({ show, onHide, order, user }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  // Envoi du lien par email au client : 'idle' | 'sending' | 'sent'
  const [emailStatus, setEmailStatus] = useState('idle');
  const [emailError, setEmailError] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  const reset = () => {
    setAmount('');
    setLoading(false);
    setError(null);
    setPaymentLink(null);
    setEmailStatus('idle');
    setEmailError(null);
    setSentTo(null);
  };

  const hide = () => {
    reset();
    onHide();
  };

  const normalizedAmount = amount.trim().replace(',', '.');
  const amountIsValid = AMOUNT_REGEX.test(normalizedAmount) && parseFloat(normalizedAmount) > 0;

  const submit = async (event) => {
    event.preventDefault();
    if (!amountIsValid || loading) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.createPaymentLink(order.id, parseFloat(normalizedAmount).toFixed(2));
      setPaymentLink(res);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!paymentLink || emailStatus === 'sending') {
      return;
    }
    setEmailStatus('sending');
    setEmailError(null);
    try {
      const res = await api.sendPaymentLinkEmail(
        order.id,
        paymentLink.url,
        (paymentLink.amount / 100).toFixed(2),
      );
      const email = res && res.email;
      setSentTo(email);
      setEmailStatus('sent');
      store.dispatch(Notifications.success({
        title: "Lien de paiement envoyé",
        message: email ? `Email envoyé à ${email}` : "Email envoyé au client",
        autoDismiss: 5,
        dismissible: "click",
      }));
    } catch (e) {
      setEmailError(extractErrorMessage(e));
      setEmailStatus('idle');
    }
  };

  if (!order) {
    return null;
  }

  const customerEmail = user && user.email;

  return (
    <Modal
      show={show}
      onHide={hide}
      animation={false}
      backdrop
      keyboard
    >
      <Modal.Header closeButton>
        <Modal.Title>Lien de paiement — commande #{order.random_id}</Modal.Title>
      </Modal.Header>

      {paymentLink ? (
        <React.Fragment>
          <Modal.Body>
            <div className="alert alert-success" role="alert">
              Lien de paiement créé pour <b>{(paymentLink.amount / 100).toFixed(2)} €</b>.
              {paymentLink.expires_on && (
                <React.Fragment>
                  <br />
                  Valable jusqu&apos;au {moment(paymentLink.expires_on).format("DD/MM/YYYY à HH:mm")}.
                </React.Fragment>
              )}
            </div>
            <Form.Label>Lien à envoyer au client</Form.Label>
            <Form.Control
              type="text"
              readOnly
              value={paymentLink.url}
              onFocus={(e) => e.target.select()}
            />
            <small className="text-muted">Référence Checkout : {paymentLink.id}</small>

            <div className="mt-3">
              <Button
                variant="primary"
                onClick={() => copyToClipboard(paymentLink.url)}
              >
                <i className="fa-regular fa-copy"></i> Copier le lien
              </Button>
              <Button
                variant="outline-primary"
                className="ml-2"
                onClick={sendEmail}
                disabled={emailStatus !== 'idle'}
                title={customerEmail ? `Envoyer le lien à ${customerEmail}` : undefined}
              >
                <i className="fa-regular fa-envelope"></i>{' '}
                {emailStatus === 'sending' && "Envoi en cours..."}
                {emailStatus === 'sent' && "Email envoyé"}
                {emailStatus === 'idle' && "Envoyer par email au client"}
              </Button>
            </div>
            {emailStatus === 'sent' && (
              <div className="alert alert-success mt-3 mb-0" role="alert">
                Le lien a été envoyé par email{sentTo ? <span> à <b>{sentTo}</b></span> : null}.
              </div>
            )}
            {emailError && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                Échec de l&apos;envoi de l&apos;email : {emailError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={hide}>Fermer</Button>
          </Modal.Footer>
        </React.Fragment>
      ) : (
        <Form onSubmit={submit}>
          <Modal.Body>
            {error && (
              <div className="alert alert-danger" role="alert">{error}</div>
            )}
            <Form.Group controlId="payment-link-amount">
              <Form.Label>Montant à faire payer au client</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="ex : 33.44"
                  value={amount}
                  autoFocus
                  disabled={loading}
                  onChange={(e) => setAmount(e.target.value)}
                  isInvalid={amount !== '' && !amountIsValid}
                />
                <InputGroup.Append>
                  <InputGroup.Text>€</InputGroup.Text>
                </InputGroup.Append>
                <Form.Control.Feedback type="invalid">
                  Montant strictement positif, 2 décimales maximum.
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={hide} disabled={loading}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={!amountIsValid || loading}>
              {loading ? "Création..." : "Créer le lien"}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

PaymentLinkModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  order: PropTypes.object,
  user: PropTypes.object,
};

export default PaymentLinkModal;
